<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Response List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Response
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="responses"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-info me-2" @click="viewResponse(item as ResponsePatient)">
                            <i class="fa fa-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-warning me-2" @click="editResponse(item as ResponsePatient)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteResponse(item as ResponsePatient)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="responseModal" tabindex="-1" ref="modalRef" aria-labelledby="responseModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveResponse">Save</button>
                    </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="viewResponseModal" tabindex="-1" ref="viewModalRef">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Bank & Account Information</h5>
                    <button type="button" class="btn-close" @click="closeViewModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedResponse">
                        <div class="mb-2"><strong>Code:</strong> {{ selectedResponse.code }}</div>
                        <div class="mb-2"><strong>Name:</strong> {{ selectedResponse.name }}</div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const responses = ref<ResponsePatient[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    code: '',
    name: ''
}

const form = reactive({
    ...initForm
})

const dataResponses: ResponsePatient[] = []
import * as master from "@/api/services/MasterRequest";

const fetchResponses = async () => {
    loading.value = true
    try {
        const resp = await master.postRequest("/response-patient/list", {...params});
        dataResponses.splice(0, dataResponses.length);
        dataResponses.push(...resp.data);

        let filteredResponses = [...dataResponses]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredResponses = dataResponses.filter(response =>
            response.code.toLowerCase().includes(searchLower) ||
            response.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        responses.value = filteredResponses.slice(start, end)
        total.value = filteredResponses.length

    } catch (error) {
        toast.error('Failed to fetch responses')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchResponses()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedResponse = ref<Bank | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editResponse = (response: ResponsePatient) => {
    openModal(response);
}

const saveResponse = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await master.postRequest("/response-patient/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Response updated successfully');
                    closeModal();
                    fetchResponses();
                } else {
                    toast.error('Failed to update Response');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update Response');
            })
        } else {
            await master.postRequest("/response-patient/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Response created successfully');
                    closeModal();
                    fetchResponses();
                } else {
                    toast.error('Failed to create response');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create response');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save response');
    } finally {
        loading.value = false
    }
}

const deleteResponse = async (response: ResponsePatient) => {
    if (!confirm('Are you sure you want to delete this response?')) return

    loading.value = true
    try {
        await master.postRequest("/response-patient/delete", {ID: response.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Response deleted successfully');
                closeModal();
                fetchResponses();
            } else {
                toast.error('Failed to delete response');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete response');
    } finally {
        loading.value = false
    }
}


const openModal = (response: ResponsePatient | null = null) => {
    if (response) {
        modalTitle.value = 'Edit Response'
        Object.assign(form, response);
    } else {
        modalTitle.value = 'Create Response'
        Object.assign(form, {...initForm})
    }
    if (bsModal) {
        bsModal.show()
    }
}

const viewResponse = (response: ResponsePatient) => {
    selectedResponse.value = response
    if (!bsViewModal && viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    if (bsViewModal) {
        bsViewModal.show()
    }
}

const closeViewModal = () => {
    if (bsViewModal) {
        bsViewModal.hide()
    }
    selectedResponse.value = null
}
</script>